/* eslint-disable @typescript-eslint/no-explicit-any */
// https://ui.shadcn.com/view/styles/new-york/sidebar-07
// https://tailwindcss.com/plus/ui-blocks/application-ui/lists/feeds

import { StackLink } from '@/app/navigation/StackLink';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import * as React from 'react';
// Import navigation abstractions
import {
  DDNodeMeta,
  DrillDownNode,
  drillDownTree as baseDrillDownTree,
  matchDrillDownPath,
  STACK_SEPARATOR,
} from '@/app/navigation';

import { useMockData } from '@/hooks/useMockData';

function WrapData({ k }: { k: string }) {
  return (
    <React.Suspense fallback={`loading ${k}...`}>
      <Data k={k} />
    </React.Suspense>
  );
}
function Data({ k }: { k: string }) {
  const d = useMockData(k);
  return (
    <>
      {k}: {d}
    </>
  );
}

function useRandomColor(): { style: Pick<React.CSSProperties, 'color'> } {
  // Pick a random color on mount
  const [color] = React.useState(() => {
    const colors = [
      '#e6194b',
      '#3cb44b',
      '#ffe119',
      '#4363d8',
      '#f58231',
      '#911eb4',
      '#46f0f0',
      '#f032e6',
      '#bcf60c',
      '#fabebe',
      '#008080',
      '#e6beff',
      '#9a6324',
      '#800000',
      '#aaffc3',
      '#808000',
      '#ffd8b1',
      '#000075',
      '#808080',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  });
  return { style: { color } };
}

// --- Panel Components (mock for POC, named per snippet) ---
const ProjectsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>ProjectsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="projects" />
        </li>
      </ul>
    </div>
  );
};
const ProjectDetailsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>ProjectDetailsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="project-details" />
        </li>
      </ul>
    </div>
  );
};
const TicketDetailsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>TicketDetailsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="ticket-details" />
        </li>
      </ul>
    </div>
  );
};
const DocsDetailsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>DocsDetailsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="docs-details" />
        </li>
      </ul>
    </div>
  );
};
const WorkDetailsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>WorkDetailsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="work-details" />
        </li>
      </ul>
    </div>
  );
};
const ChecklistDetailsComponent = ({ params }: { params: any }) => {
  const clr = useRandomColor();
  return (
    <div {...clr}>
      <strong>ChecklistDetailsComponent</strong>
      <ul>
        <li>params: {JSON.stringify(params)}</li>
        <li>
          <WrapData k="checklist-details" />
        </li>
      </ul>
    </div>
  );
};

// Assign the correct component to each node in the tree at runtime
function assignComponentsToTree(
  tree: DrillDownNode<DDNodeMeta>[],
): DrillDownNode<DDNodeMeta>[] {
  // Map of label to component
  const labelToComponent: Record<
    string,
    React.ComponentType<{ params: any }>
  > = {
    Projects: ProjectsComponent,
    'Project Details': ProjectDetailsComponent,
    'Ticket Details': TicketDetailsComponent,
    'Docs Details': DocsDetailsComponent,
    'Work Details': WorkDetailsComponent,
    'Checklist Details': ChecklistDetailsComponent,
  };
  function assign(node: DrillDownNode<DDNodeMeta>): DrillDownNode<DDNodeMeta> {
    return {
      ...node,
      component: labelToComponent[node.label] || node.component,
      children: node.children
        ? (node.children.map(assign) as any)
        : node.children,
    };
  }
  return tree.map(assign);
}

const drillDownTree = assignComponentsToTree(baseDrillDownTree);

// --- Helper to match a path to a list of panel nodes (supports prefix matching) ---
// (Now imported from navigation)

function DrillDownStack({
  segments,
  label,
}: {
  segments: string[];
  label: string;
}) {
  const matched = matchDrillDownPath(drillDownTree, segments);
  return (
    <div style={{ border: '1px solid #ccc', margin: 8, padding: 8 }}>
      <strong>{label}</strong>
      <ol>
        {matched.length === 0 && <li key="empty">(empty)</li>}
        {matched.filter(Boolean).map((item, i) => {
          const { node, params } = item;
          const Panel = node.component;
          // Key is just the logical path
          const key = matched
            .slice(0, i + 1)
            .map(({ node, params }) =>
              node.segment.startsWith(':')
                ? params[node.segment.slice(1)]
                : node.segment,
            )
            .join('/');
          return (
            <li key={key}>
              {Panel ? (
                <Panel params={params} key={key} />
              ) : (
                <div>No component</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function POCApp() {
  const location = useLocation();
  const navigate = useNavigate();
  // Split at the first occurrence of '~' (STACK_SEPARATOR)
  const [primary, secondary] = React.useMemo(() => {
    const idx = location.pathname.indexOf(STACK_SEPARATOR);
    if (idx === -1) return [location.pathname, ''];
    return [
      location.pathname.slice(0, idx),
      location.pathname.slice(idx + STACK_SEPARATOR.length),
    ];
  }, [location.pathname]);
  const primarySegments = primary.split('/').filter(Boolean);
  const secondarySegments = secondary.split('/').filter(Boolean);

  // Helper to build a link that opens in the secondary stack
  // const openInSecondary = (secondaryPath: string) => {
  //   // Always keep the current primary
  //   return `${primary}${STACK_SEPARATOR}${secondaryPath}`;
  // };

  // Helper to build a link that navigates the primary stack
  const openInPrimary = (primaryPath: string) => {
    // Keep the current secondary
    return `${primaryPath}${secondary ? STACK_SEPARATOR + secondary : ''}`;
  };

  // --- Stack key management ---
  // Generate a persistent random key for each stack on first render
  const [stackKeys, setStackKeys] = React.useState(() => ({
    primary: Math.random().toString(36).slice(2),
    secondary: Math.random().toString(36).slice(2),
  }));

  // Do NOT update keys on navigation within a stack
  // Only swap keys when swapping stacks

  // Swap stacks handler
  const swapStacks = () => {
    if (!primary && !secondary) return;
    // Swap both the stack paths and their keys
    setStackKeys((keys) => ({
      primary: keys.secondary,
      secondary: keys.primary,
    }));
    const newPath = secondary
      ? `/${secondary}${primary ? STACK_SEPARATOR + primary : ''}`
      : `${STACK_SEPARATOR}${primary}`;
    navigate({ to: newPath });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <DrillDownStack
          key={stackKeys.primary}
          segments={primarySegments}
          label="Primary stack"
        />
        <DrillDownStack
          key={stackKeys.secondary}
          segments={secondarySegments}
          label="Secondary stack"
        />
        <div style={{ marginLeft: 32 }}>
          <div>
            <strong>Test Links</strong>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects')}>
              Go to /projects
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123')}>
              Go to /projects/123
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/tickets')}>
              Go to /projects/123/tickets (tab)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/tickets/25')}>
              Go to /projects/123/tickets/25 (ticket details)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/docs')}>
              Go to /projects/123/docs (tab)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/docs/42')}>
              Go to /projects/123/docs/42 (doc details)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/works')}>
              Go to /projects/123/works (tab)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/works/456')}>
              Go to /projects/123/works/456
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/works/456/tickets')}>
              Go to /projects/123/works/456/tickets (tab)
            </StackLink>
          </div>
          <div>
            <StackLink
              to={openInPrimary('/projects/123/works/456/tickets/789')}
            >
              Go to /projects/123/works/456/tickets/789 (work ticket details)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/works/456/lists')}>
              Go to /projects/123/works/456/lists (tab)
            </StackLink>
          </div>
          <div>
            <StackLink to={openInPrimary('/projects/123/works/456/lists/999')}>
              Go to /projects/123/works/456/lists/999 (list details)
            </StackLink>
          </div>
          <div>
            <StackLink
              to={openInPrimary('/projects/123/works/456/lists/999/3')}
            >
              Go to /projects/123/works/456/lists/999/3 (list question)
            </StackLink>
          </div>
        </div>
        <button
          onClick={swapStacks}
          style={{ alignSelf: 'flex-start', marginBottom: 8 }}
        >
          Swap Stacks
        </button>
      </div>
    </div>
  );
}

// Set up the root and catch-all route for the POC
const rootRoute = createRootRoute();

const pocRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/*',
  component: POCApp,
});

const routeTree = rootRoute.addChildren([pocRoute]);

const router = createRouter({
  routeTree,
});

function App() {
  return (
    <QueryClientProvider client={new QueryClient()}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
      <TanStackRouterDevtools router={router} initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
